/**
 * System → ID & Number Formats.
 *
 * How many records hold an identifier as a number instead of text.
 *
 * The 2026-08-27 letters outage was one employee whose employee number was
 * stored as 27523 rather than "27523". Every screen now accepts both, so this
 * is not a fault list — it is the measure of how much stored data still needs
 * converting, and the number to watch before and after any conversion.
 *
 * Read-only. Nothing on this screen changes stored data.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, Hash, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../../components/ui/card';
import { useLanguage } from '../../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FIELD_NAMES = {
  'Employee number': 'رقم الموظف',
  'National ID': 'رقم الهوية',
  'Iqama': 'رقم الإقامة',
  'Passport number': 'رقم جواز السفر',
  'GOSI number': 'رقم التأمينات',
  'Phone': 'رقم الجوال',
  'Emergency contact phone': 'جوال جهة الطوارئ',
  'IBAN': 'الآيبان',
  'National ID (login account)': 'رقم الهوية (حساب الدخول)',
  'Iqama (login account)': 'رقم الإقامة (حساب الدخول)',
  'Two-step mobile': 'جوال التحقق بخطوتين',
};

export default function IdentifierFormatsPage() {
  const { isRTL } = useLanguage();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/data-health/identifier-formats`);
      setReport(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.error
        || (isRTL ? 'تعذّر تحميل التقرير' : 'Could not load the report'));
    } finally {
      setLoading(false);
    }
  }, [isRTL]);

  useEffect(() => { load(); }, [load]);

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="id-formats-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const clean = report?.clean;

  return (
    <div className="space-y-6" data-testid="identifier-formats-page">
      <Card className={clean ? 'border-emerald-300' : 'border-amber-300'}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Hash className="h-5 w-5" />
            {isRTL ? 'تنسيق الأرقام والهويات' : 'ID & number formats'}
            <Button
              variant="ghost"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="id-formats-refresh"
            >
              {loading
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <RefreshCw className="h-4 w-4" />}
            </Button>
          </CardTitle>
          <CardDescription>
            {isRTL
              ? 'الهوية والإقامة ورقم الموظف والجوال نصوص، لا أرقام. كل الشاشات تقبل الصيغتين، وهذه الأعداد تُبيّن ما يحتاج تحويلاً لاحقاً. هذه الشاشة للقراءة فقط ولا تُعدّل أي بيانات.'
              : 'ID, Iqama, employee number and phone are text, not numbers. Every screen accepts both formats — these counts show how much stored data still needs converting. Read-only: nothing here changes your data.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${clean
              ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
              : 'border-amber-300 bg-amber-50 text-amber-900'}`}
            data-testid="id-formats-headline"
          >
            {clean
              ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
              : <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />}
            <span>
              {clean
                ? (isRTL
                  ? 'كل الهويات والأرقام محفوظة كنصوص. لا يوجد ما يحتاج تحويلاً.'
                  : 'Every identifier is stored as text. There is nothing to convert.')
                : (isRTL
                  ? `${report.total_numeric_values} قيمة محفوظة كرقم، تخص ${report.employees_affected} من ${report.employees_total} موظف و${report.accounts_affected} من ${report.accounts_total} حساب دخول. لا شيء معطّل — الشاشات تقبل الصيغتين.`
                  : `${report.total_numeric_values} value(s) are stored as numbers, across ${report.employees_affected} of ${report.employees_total} employees and ${report.accounts_affected} of ${report.accounts_total} login accounts. Nothing is broken — every screen accepts both.`)}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { key: 'total_numeric_values',
                label: { en: 'Stored as a number', ar: 'محفوظة كرقم' } },
              { key: 'employees_affected',
                label: { en: 'Employees affected', ar: 'موظفون متأثرون' } },
              { key: 'accounts_affected',
                label: { en: 'Login accounts affected', ar: 'حسابات دخول متأثرة' } },
              { key: 'employees_total',
                label: { en: 'Employees in total', ar: 'إجمالي الموظفين' } },
            ].map(({ key, label }) => (
              <div key={key} className="rounded-lg border px-3 py-2" data-testid={`id-formats-${key}`}>
                <div className="text-2xl font-semibold">{report?.[key] ?? 0}</div>
                <div className="text-xs text-muted-foreground">
                  {isRTL ? label.ar : label.en}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card data-testid="id-formats-table">
        <CardHeader>
          <CardTitle className="text-lg">
            {isRTL ? 'حسب الحقل' : 'By field'}
          </CardTitle>
          <CardDescription>
            {isRTL
              ? 'يُحدَّد النوع من طريقة الحفظ في قاعدة البيانات، لا من شكل القيمة.'
              : 'Judged by how the value is stored in the database, not by how it looks.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-start text-muted-foreground">
                  <th className="py-2 text-start font-medium">
                    {isRTL ? 'الحقل' : 'Field'}
                  </th>
                  <th className="py-2 text-end font-medium">
                    {isRTL ? 'كرقم' : 'As a number'}
                  </th>
                  <th className="py-2 text-end font-medium">
                    {isRTL ? 'كنص' : 'As text'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(report?.fields || []).map((field) => (
                  <tr
                    key={`${field.collection}.${field.path}`}
                    className="border-b last:border-0"
                    data-testid={`id-formats-row-${field.path}`}
                  >
                    <td className="py-2">
                      {isRTL ? (FIELD_NAMES[field.label] || field.label) : field.label}
                      <span className="ms-2 text-xs text-muted-foreground" dir="ltr">
                        {field.collection}.{field.path}
                      </span>
                    </td>
                    <td className="py-2 text-end">
                      {field.numeric > 0 ? (
                        <Badge className="bg-amber-100 text-amber-900">{field.numeric}</Badge>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </td>
                    <td className="py-2 text-end text-muted-foreground">{field.text}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
